// An aura chip: name, an editable countdown (click the number: 15 sets it,
// +5 / -3 adjust; 0 ends it), the 🎲 that says who rolls its dice, and ×.
// The 🎲 is off by default (the DM Screen rolls on every turn change); on,
// the player rolls at the table and the DM Screen only records chat rolls.
import { useEffect, useState } from "react";
import { AURAS, auraRollKey, endAura, setAuraRemaining, type ActiveAura } from "../state/auras";
import { useStore } from "../state/store";
import type { Character } from "../state/types";
import { NumberField } from "./NumberField";
import { carriedItemNames, carriedLights, gearDouseKey, LIGHTS, lightFromGear, setLightRemaining, startLight, withTaughtLight, type ActiveLight, type LightDef } from "../state/lighting";
import { isRangedMode, PROFILE_LABEL, RANGED_MODES, type VisionProfile } from "../state/vision";
import { ABILITIES, ABILITY_LABEL, rollSave, saveBonusFor, sign, type SaveBonus, type SaveRoll } from "../state/rowSave";
import { candidatesFor, resolveSave, SAVE_SOURCE_LABEL, type MonsterSaveData, type SaveSource } from "../state/monsterSave";
import { monsterSaveData } from "../state/monsterSaveSources";
import type { AbilityKey } from "../state/types";

export function AuraRollToggle({ characterId, characterName, auraKey }: { characterId: string; characterName: string; auraKey: string }) {
  const { layout, update, logActivity } = useStore();
  const def = AURAS.find((d) => d.key === auraKey);
  if (!def?.dice) return null;
  const prefs = (layout.auraPlayerRolls || {}) as Record<string, boolean>;
  const key = auraRollKey(characterId, auraKey);
  const player = Boolean(prefs[key]);
  const when = def.rollAt === "casterTurnEnd" ? "at the end of the caster's turn" : "on every turn change";
  const flip = () => {
    update((l) => ({ ...l, auraPlayerRolls: { ...((l.auraPlayerRolls as Record<string, boolean>) || {}), [key]: !player } }));
    logActivity({ text: `${characterName}: ${def.name} dice`, meta: player ? `the DM Screen rolls them ${when}` : "the player rolls them at the table; chat rolls are recorded", tone: "violet" });
  };
  return <button type="button" className={`roller ${player ? "player" : ""}`} onClick={flip} aria-pressed={player} aria-label={`${def.name}: ${player ? "the player rolls" : "the DM Screen rolls"} (click to switch)`} title={player ? `The player rolls this ability at the table; rolls posted to Roll20 chat are picked up. Click: let the DM Screen roll it ${when}.` : `The DM Screen rolls this ability ${when} and shows the number on the token. Click: the player wants to roll it.`}>🎲</button>;
}

export function AuraChip({ c, a }: { c: Character; a: ActiveAura }) {
  const { update, logActivity } = useStore();
  const setChar = (fn: (cur: Character) => Partial<Character>) => update((l) => ({ ...l, characters: l.characters.map((x) => (x.id === c.id ? { ...x, ...fn(x) } : x)) }));
  const timed = a.remaining !== null && a.remaining !== undefined;
  const title = `${a.radius} ft${a.concentration ? " · concentration" : ""}${timed ? ` · ${a.remaining} more rounds (click the number to set it; an hour is 600)` : " · passive"}`;
  return (
    <span className="chip aura" style={{ borderColor: a.color }} title={title}>
      {a.name}
      {timed ? <>{" ("}<NumberField value={Number(a.remaining)} label={`${a.name} rounds left`} min={0} onApply={(n) => { setChar((cur) => setAuraRemaining(cur, a.key, n)); logActivity({ text: `${c.name}: ${a.name}`, meta: n > 0 ? `clock set to ${n} rounds` : "ended", tone: "violet" }); }} />{")"}</> : ` ${a.radius} ft`}
      {a.roll !== undefined && a.roll !== null && <> · {a.roll}{a.rollUnit ? ` ${a.rollUnit}` : ""}</>}
      <AuraRollToggle characterId={c.id} characterName={c.name} auraKey={a.key} />
      <button aria-label={`End ${a.name}`} title={`End ${a.name} now (clears its ring and tooltip entry)`} onClick={() => { setChar((cur) => endAura(cur, a.key)); logActivity({ text: `${c.name}: ${a.name} ended`, tone: "green" }); }}>×</button>
    </span>
  );
}

/** A carried light: name, burn-down (click to set), and × to put it out. */
export function LightChipView({ name, light, onSet }: { name: string; light: ActiveLight; onSet: (next: ActiveLight | undefined) => void }) {
  const { update, logActivity } = useStore();
  const l = light;
  const timed = l.remaining !== null && l.remaining !== undefined;
  // "−" ends the source for good. A carried/taught light is re-lit every sync by
  // the gear sweep (Shell.tsx), so putting it out (×) is not enough — forget the
  // lesson that this item sheds light, then clear it; the next sweep finds no
  // source and leaves it dark. A no-op for a built-in light (nothing taught under
  // that name), so it is safe to show on every chip.
  const end = () => {
    update((lay) => withTaughtLight(lay, l.gear || l.name, null));
    onSet(undefined);
    logActivity({ text: `${name}: ${l.name} ended`, meta: "no longer treated as a light source", tone: "green" });
  };
  return (
    <span className="chip light" title={`${l.bright} ft bright, ${l.dim} ft dim beyond${timed ? ` · ${l.remaining} more rounds (click the number to set it; an hour is 600)` : ""}`}>
      <button className="link small" aria-label={`End ${l.name} as a light source`} title="End this light source" onClick={end}>−</button>
      🔥 {l.name}
      {timed && <>{" ("}<NumberField value={Number(l.remaining)} label={`${l.name} rounds left`} min={0} onApply={(n) => { const next = setLightRemaining(l, n); onSet(next); logActivity({ text: `${name}: ${l.name}`, meta: next ? `burns for ${n} more rounds` : "put out", tone: "violet" }); }} />{")"}</>}
      <button aria-label={`Put out ${l.name}`} title={`Put out the ${l.name}`} onClick={() => { onSet(undefined); logActivity({ text: `${name}: ${l.name} put out`, tone: "green" }); }}>×</button>
    </span>
  );
}
export function LightPickerView({ name, onPick, onClear }: { name: string; onPick: (light: ActiveLight, def: LightDef) => void; onClear?: () => void }) {
  const { logActivity } = useStore();
  return (
    <select value="" className="light-pick" aria-label={`Light a source on ${name}`} title="Light the token: bright and dim radius as Roll20's dynamic lighting; timed sources burn down on the initiative clock (rounds). Remove light clears any light the token carries in Roll20." onChange={(e) => { if (e.target.value === "__clear") { onClear?.(); logActivity({ text: `${name}: light removed`, meta: "the token emits no light", tone: "green" }); return; } const def = LIGHTS.find((d) => d.key === e.target.value); if (!def) return; onPick(startLight(def), def); logActivity({ text: `${name}: ${def.name}`, meta: `${def.bright} ft bright / ${def.dim} ft dim${def.rounds ? ` for ${def.rounds} turns` : ""}`, tone: "violet" }); }}>
      <option value="">+ light</option>
      {onClear && <option value="__clear">✕ remove light (clear the token)</option>}
      {LIGHTS.map((d) => <option key={d.key} value={d.key}>{d.name} ({d.bright}/{d.dim})</option>)}
    </select>
  );
}

/** The light a party member carries (state on the character; the bar sync writes it). */
export function LightChip({ c }: { c: Character }) {
  const { update } = useStore();
  const l = c.light as ActiveLight | undefined;
  if (!l) return null;
  // Putting out a GEAR light is remembered (lightDoused), so the automatic
  // sweep does not relight an item that is still equipped on the sheet; a
  // hand-set light carries no `gear` mark and leaves no marker. Relighting
  // anything clears the marker.
  const setLight = (next: ActiveLight | undefined) => update((s) => ({ ...s, characters: s.characters.map((x) => {
    if (x.id !== c.id) return x;
    const { light: _drop, lightDoused: _wasDoused, ...rest } = x as Character;
    if (next) return { ...rest, light: next };
    return l.gear ? { ...rest, lightDoused: gearDouseKey(l) } : (rest as Character);
  }) }));
  return <LightChipView name={c.name} light={l} onSet={setLight} />;
}
/** The light picker for a PARTY MEMBER, who has an inventory. Their own gear
 *  comes first — a torch they are holding, a Driftglobe they have attuned —
 *  because "what is this character carrying that glows" is the question the
 *  DM is actually asking. An item the table does not know can be taught once
 *  and is remembered by name for every character afterwards. */
export function LightPicker({ c, onClear }: { c: Character; onClear?: () => void }) {
  const { layout, update, logActivity } = useStore();
  const [teaching, setTeaching] = useState<{ item: string; bright: string; dim: string; burns: boolean } | null>(null);
  const carried = carriedLights(c, layout);
  // Relighting by hand clears any "put out" marker the sweep was honouring.
  const setLight = (light: ActiveLight) => update((s) => ({ ...s, characters: s.characters.map((x) => { if (x.id !== c.id) return x; const { lightDoused: _d, ...rest } = x as Character; return { ...rest, light }; }) }));
  const unknown = carriedItemNames(c).filter((n) => !carried.some((g) => g.item === n));
  const commitTeach = () => {
    if (!teaching) return;
    const bright = Math.max(0, Number(teaching.bright) || 0), dim = Math.max(0, Number(teaching.dim) || 0);
    setTeaching(null);
    if (!bright && !dim) return;
    const rounds = teaching.burns ? 600 : 0;
    update((l) => withTaughtLight(l, teaching.item, { name: teaching.item, bright, dim, rounds }));
    setLight({ key: `taught:${teaching.item.toLowerCase()}`, name: teaching.item, bright, dim, remaining: rounds || null, total: rounds || undefined, since: new Date().toISOString(), gear: teaching.item });
    logActivity({ text: `${teaching.item}: ${bright} ft bright / ${dim} ft dim`, meta: `taught to the table — every character carrying one lights the same from now on${rounds ? "; burns for an hour" : ""}`, tone: "violet" });
  };
  if (teaching) return (
    <span className="duration" onClick={(e) => e.stopPropagation()}>
      <strong>{teaching.item}</strong> sheds
      <input className="xs" type="number" min={0} step={5} autoFocus value={teaching.bright} aria-label="Bright light radius in feet" onChange={(e) => setTeaching({ ...teaching, bright: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") commitTeach(); if (e.key === "Escape") setTeaching(null); }} /> ft bright,
      <input className="xs" type="number" min={0} step={5} value={teaching.dim} aria-label="Dim light radius in feet" onChange={(e) => setTeaching({ ...teaching, dim: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") commitTeach(); if (e.key === "Escape") setTeaching(null); }} /> ft dim
      <label className="check" title="On: it burns down on the initiative clock like a torch (an hour). Off: magical, it just stays lit."><input type="checkbox" checked={teaching.burns} onChange={(e) => setTeaching({ ...teaching, burns: e.target.checked })} /> burns out</label>
      <button className="small primary" onClick={commitTeach}>Light it</button><button className="ghost small" onClick={() => setTeaching(null)}>Cancel</button>
    </span>
  );
  return (
    <select value="" className="light-pick" aria-label={`Light a source on ${c.name}`} title="Light the token. The character's own gear comes first; anything else can be taught once and is remembered by name." onChange={(e) => {
      const v = e.target.value; e.target.value = "";
      if (!v) return;
      if (v === "__clear") { onClear?.(); logActivity({ text: `${c.name}: light removed`, meta: "the token emits no light", tone: "green" }); return; }
      if (v.startsWith("teach:")) { setTeaching({ item: v.slice(6), bright: "20", dim: "20", burns: false }); return; }
      if (v.startsWith("gear:")) {
        const g = carried.find((x) => x.item === v.slice(5)); if (!g) return;
        setLight(lightFromGear(g));
        logActivity({ text: `${c.name} lights ${g.item}`, meta: `${g.bright} ft bright / ${g.dim} ft dim${g.rounds ? ` for ${g.rounds} turns` : ""}`, tone: "violet" });
        return;
      }
      const def = LIGHTS.find((d) => d.key === v); if (!def) return;
      setLight(startLight(def));
      logActivity({ text: `${c.name}: ${def.name}`, meta: `${def.bright} ft bright / ${def.dim} ft dim${def.rounds ? ` for ${def.rounds} turns` : ""}`, tone: "violet" });
    }}>
      <option value="">+ light</option>
      {onClear && <option value="__clear">✕ remove light (clear the token)</option>}
      {carried.length > 0 && <optgroup label={`${c.name} is carrying`}>
        {carried.map((g) => <option key={g.item} value={`gear:${g.item}`}>{g.item} ({g.bright}/{g.dim}){g.on ? " · in use" : ""}</option>)}
      </optgroup>}
      <optgroup label="Any source">
        {LIGHTS.map((d) => <option key={d.key} value={d.key}>{d.name} ({d.bright}/{d.dim})</option>)}
      </optgroup>
      {unknown.length > 0 && <optgroup label="Teach the table what an item does">
        {unknown.map((n) => <option key={n} value={`teach:${n}`}>{n}…</option>)}
      </optgroup>}
    </select>
  );
}

/** 👁 vision profile for a row: by stat block (default), sight only, night vision N ft, or none. */
export function VisionPicker({ name, profile, onChange }: { name: string; profile: VisionProfile | undefined; onChange: (next: VisionProfile | undefined) => void }) {
  const mode = profile?.mode || "statblock";
  return (
    <span className="vision-pick" title="Token vision: by stat block (the sheet's senses through the source order under Roll20 options), sight only, night vision of a set range, or none. Written to the token at once.">
      <span aria-hidden="true">👁</span>
      <select className="xs" value={mode} aria-label={`Vision profile for ${name}`} onChange={(e) => { const m = e.target.value as VisionProfile["mode"]; onChange(m === "statblock" ? undefined : { mode: m, night: isRangedMode(m) ? Number(profile?.night ?? RANGED_MODES[m]) : undefined }); }}>
        {(Object.keys(PROFILE_LABEL) as VisionProfile["mode"][]).map((m) => <option key={m} value={m}>{PROFILE_LABEL[m]}</option>)}
      </select>
      {isRangedMode(mode) && <input className="xs" type="number" min={0} step={5} value={Number(profile?.night ?? RANGED_MODES[mode])} aria-label={`${PROFILE_LABEL[mode]} range for ${name}`} onChange={(e) => onChange({ mode, night: Math.max(0, Number(e.target.value) || 0) })} />}
    </span>
  );
}

/* ROLL A SAVE FROM THE ROW.

   Reported 2026-10-01: the DM wants the save for whoever is in front of them
   rolled off that token's own scores, with a plain d20 when there are none and
   a bonus they can set on the fly.

   The basis is shown, not hidden, because a sheet's +7 and a bare d20 are
   different answers and "+0" looks like a fact while "no sheet" is a question. */
export function SaveRoller({ name, character, onRolled, monster, precedence, askEveryTime }: { name: string; character: Character | null | undefined; onRolled: (ability: AbilityKey, roll: SaveRoll, bonus: SaveBonus) => void; monster?: boolean; precedence?: SaveSource[]; askEveryTime?: boolean }) {
  const [ability, setAbility] = useState<AbilityKey>("dex");
  const [extra, setExtra] = useState(0);
  /* A monster row has no sheet of its own; its save may come from D&D Beyond,
     the Compass, or Roll20. Fetch what every capable source holds ONCE per
     creature (monsterSaveData caches + gates on capability), then reconcile it
     per-ability, synchronously, as the DM flips the ability select. PCs keep the
     old path untouched — sources stays null and saveBonusFor(character) wins. */
  const [sources, setSources] = useState<MonsterSaveData[] | null>(null);
  useEffect(() => {
    if (!monster || character) { setSources(null); return; }
    let live = true;
    void monsterSaveData(name).then((d) => { if (live) setSources(d); });
    return () => { live = false; };
  }, [monster, character, name]);

  const flat = saveBonusFor(character, ability, extra);
  const resolution = monster && !character && sources ? resolveSave(candidatesFor(ability, sources, extra), precedence ?? [], askEveryTime ?? true) : null;
  const bonus = resolution && resolution.kind === "use" ? resolution.candidate.bonus : flat;
  const asking = resolution && resolution.kind === "ask" ? resolution.candidates : null;

  return (
    <span className="save-roll" title={`Roll a ${ABILITY_LABEL[ability]} saving throw for ${name}. ${asking ? `${asking.length} sources — pick one` : bonus.why}`}>
      <select className="xs" value={ability} aria-label={`Saving throw ability for ${name}`} onChange={(e) => setAbility(e.target.value as AbilityKey)}>
        {ABILITIES.map((k) => <option key={k} value={k}>{ABILITY_LABEL[k]}</option>)}
      </select>
      <button className="link small" aria-label={`Lower the extra bonus for ${name}`} onClick={() => setExtra((n) => n - 1)}>−</button>
      <button className="link small" aria-label={`Raise the extra bonus for ${name}`} onClick={() => setExtra((n) => n + 1)}>+</button>
      {asking
        ? <span className="save-ask" role="group" aria-label={`Pick a save source for ${name}`}>
            {asking.map((c) => (
              <button key={c.source} className={`small save-src basis-${c.bonus.basis}`} title={c.bonus.why} onClick={() => onRolled(ability, rollSave(c.bonus), c.bonus)}>
                {SAVE_SOURCE_LABEL[c.source]} {sign(c.bonus.bonus)}
              </button>
            ))}
          </span>
        : <>
            <span className={`save-bonus basis-${bonus.basis}`}>{sign(bonus.bonus)}</span>
            <button className="small" onClick={() => onRolled(ability, rollSave(bonus), bonus)}>Save</button>
          </>}
    </span>
  );
}
